/**
 * GameModeController - Mode Selection and Switching UI
 *
 * Phase 5 - SOC Refactoring (UI Cleanup)
 *
 * Extracted from BoardContainer.tsx to centralize game mode switching UI.
 * Handles all mode-related buttons: Live, Dev, AI, TestAI, Watch
 *
 * RESPONSIBILITIES:
 * - Render mode switching buttons
 * - Handle game creation and joining
 * - Manage link/ID copying
 * - Navigate to other app sections
 */

import React, { useState } from 'react';
import { Mode } from '../../services/zodiacEngine/types';
import styles from './GameModeController.module.css';

interface GameModeControllerProps {
  currentMode: Mode;
  gameId: string | null | undefined;
  darkMode: boolean;
  onCreateGame: () => void;
  onJoinGame: (gameId: string) => void;
  onDevMode: () => void;
  onAIMode: () => void;
  onTestAIMode: () => void;
  onWatchMode: () => void;
  onCopyLink: () => Promise<void>;
}

export function GameModeController({
  currentMode,
  gameId,
  darkMode,
  onCreateGame,
  onJoinGame,
  onDevMode,
  onAIMode,
  onTestAIMode,
  onWatchMode,
  onCopyLink,
}: GameModeControllerProps) {
  const [joinId, setJoinId] = useState('');
  const [copied, setCopied] = useState(false);

  const handleJoinIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setJoinId(event.target.value);
  };

  const handleJoinGame = () => {
    if (joinId.trim()) {
      onJoinGame(joinId.trim());
    }
  };

  const handleCopyLink = async () => {
    await onCopyLink();
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={styles.modeControls}>
      <h1>Zodiac</h1>

      {/* Create/Join Game */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button onClick={onCreateGame} disabled={currentMode === Mode.Live}>
          Create Game
        </button>
      </div>

      <button onClick={handleCopyLink}>
        {copied ? 'Copied!' : 'Copy link'}
      </button>

      <p />

      <button onClick={handleJoinGame}>Join Game</button>
      <input type="text" value={joinId} onChange={handleJoinIdChange} />

      <p />

      {/* Navigation Links */}
      <a
        href="/main"
        className={styles.navLink}
        style={{ backgroundColor: '#FF6B35', fontWeight: 'bold' }}
      >
        🏠 Main Page
      </a>

      <p />

      <button onClick={onDevMode}>Return to Dev Mode</button>

      <p />

      <a href="/how-to-play" className={styles.navLink}>
        How to Play
      </a>

      <p />

      <a href="/tutorials" className={styles.navLink}>
        Tutorials
      </a>

      <p />

      <a href="/shop" className={styles.navLink}>
        🛒 Shop
      </a>

      <p />

      <a href="/collection" className={styles.navLink}>
        🎒 Collection
      </a>

      <p />

      <a href="/history" className={styles.navLink}>
        📜 History
      </a>

      <p />

      <a href="/analytics" className={styles.navLink}>
        📊 Analytics
      </a>

      <p />

      <a href="/courses" className={styles.navLink}>
        🎓 Courses
      </a>

      <p />

      {/* AI Mode Buttons */}
      <button
        onClick={onAIMode}
        className={styles.navLink}
        style={{ backgroundColor: '#1E90FF' }}
      >
        Play vs AI
      </button>

      <button
        onClick={onTestAIMode}
        className={styles.navLink}
        style={{ backgroundColor: '#32CD32', marginTop: 4 }}
      >
        Test AI
      </button>

      <button
        onClick={onWatchMode}
        className={styles.navLink}
        style={{ backgroundColor: '#FF6347', marginTop: 4 }}
      >
        Watch AI vs AI
      </button>
    </div>
  );
}
